import { Analysis, type BestEfforts } from "../activity";
import React from "react";
import DataContext, { Grouped } from "./chart/DataContext";
import SizedSVG from "./chart/SizedSVG";
import Coordinates from "./chart/Coordinates";
import CoordinateContext from "./chart/CoordinateContext";
import LineSegments from "./chart/LineSegments";
import { GestureEvent } from "../hooks/useMapGestures";
import _ from "lodash";
import TextWithBackground, {
  TextWithBackgroundRef,
} from "./chart/TextWithBackground";
import { Link } from "rhoto-router";
import { useAPIData } from "../api";
import { Summary } from "../activity";

export interface NewRecord {
  duration: number;
  currentEffort: number;
  lookbackDays: number;
  previousBest: number;
}

export interface EffortDataPoint {
  duration: number;
  lookbackDays: number;
  effort: number;
  activityId?: number; // Activity ID where this effort occurred (undefined for current activity)
}

// Color scale for lookback windows (ordered by recency)
function getLookbackColor(lookbackDays: number): string {
  if (lookbackDays === 0) return "#2563eb"; // blue for current
  if (lookbackDays <= 30) return "#64748b"; // slate for 30d
  if (lookbackDays <= 90) return "#475569"; // darker slate for 90d
  return "#334155"; // darkest slate for 365d+
}

// Format lookback days for display
function formatLookback(lookbackDays: number): string {
  if (lookbackDays === 0) return "current";
  return `${lookbackDays}d`;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  } else if (seconds < 3600) {
    const minutes = seconds / 60;
    return minutes % 1 === 0 ? `${minutes}min` : `${minutes.toFixed(1)}min`;
  } else {
    const hours = seconds / 3600;
    return hours % 1 === 0 ? `${hours}hr` : `${hours.toFixed(1)}hr`;
  }
}

function transformToChartData(analysis: Analysis): EffortDataPoint[] {
  if (!analysis.bestEfforts) return [];

  const { durations, maxEfforts } = analysis.bestEfforts;
  const chartData: EffortDataPoint[] = [];

  maxEfforts.forEach(({ lookbackDays, values, activityIds }) => {
    values.forEach((effort, idx) => {
      chartData.push({
        duration: durations[idx],
        lookbackDays,
        effort,
        activityId: activityIds?.[idx],
      });
    });
  });

  return chartData;
}

export function findNewRecords(
  bestEfforts: BestEfforts | undefined,
): NewRecord[] {
  if (!bestEfforts) return [];

  const { durations, maxEfforts } = bestEfforts;
  const currentWindow = maxEfforts.find((w) => w.lookbackDays === 0);
  if (!currentWindow) return [];

  const newRecords: NewRecord[] = [];

  // Get historical windows sorted by lookback days descending (longest first)
  const historicalWindows = maxEfforts
    .filter((w) => w.lookbackDays > 0)
    .sort((a, b) => b.lookbackDays - a.lookbackDays);

  durations.forEach((duration, idx) => {
    const currentEffort = currentWindow.values[idx];

    // Check each window (longest first) to find if this is a new record
    for (const window of historicalWindows) {
      const previousBest = window.values[idx];

      // New record if current effort equals or exceeds previous best
      if (currentEffort >= previousBest) {
        newRecords.push({
          duration,
          currentEffort,
          lookbackDays: window.lookbackDays,
          previousBest,
        });
        break; // Only report the longest window where it's a new record
      }
    }
  });

  return newRecords;
}

// Component to render shaded areas and circles for new records
function RecordHighlights({
  newRecords,
  chartData,
}: {
  newRecords: NewRecord[];
  chartData: EffortDataPoint[];
}) {
  const { xScale, yScale } = React.useContext(CoordinateContext);

  // Group consecutive new records by which lookback window they beat
  const recordGroups = new Map<number, number[]>();

  newRecords.forEach((record) => {
    const existing = recordGroups.get(record.lookbackDays) || [];
    existing.push(record.duration);
    recordGroups.set(record.lookbackDays, existing);
  });

  return (
    <>
      {newRecords.map((record) => {
        const point = chartData.find(
          (d) => d.duration === record.duration && d.lookbackDays === 0,
        );
        if (!point) return null;

        return (
          <text
            key={`star-${record.duration}`}
            x={xScale({ duration: record.duration })}
            y={yScale({ effort: point.effort })}
            fontSize={12}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            🌟
          </text>
        );
      })}
    </>
  );
}

// Chart component for best efforts visualization
function BestEffortsChart({
  chartData,
  durations,
  newRecords,
  userId,
}: {
  chartData: EffortDataPoint[];
  durations: number[];
  newRecords: NewRecord[];
  userId: number;
}) {
  const [hover, setHover] = React.useState<GestureEvent | null>(null);
  const [hoverDuration, setHoverDuration] = React.useState<
    number | undefined
  >();

  return (
    <>
      <div className="effort-density" style={{ height: "100px" }}>
        <DataContext.Provider value={chartData}>
          <SizedSVG
            bottomPad={25}
            topPad={5}
            leftPad={5}
            rightPad={5}
            onHover={setHover}
          >
            <Coordinates<EffortDataPoint>
              x="duration"
              y="effort"
              colorScale={(d) => getLookbackColor(d.lookbackDays)}
            >
              {({ xScale, height, width }) => {
                React.useEffect(() => {
                  setHoverDuration(
                    hover === null
                      ? undefined
                      : _.minBy(durations, (duration) =>
                          Math.abs(xScale({ duration }) - hover.x),
                        ),
                  );
                }, [durations, hover, xScale]);

                let labelX = xScale({ duration: hoverDuration });
                const twbRef = React.useRef<TextWithBackgroundRef | null>(null);

                if (twbRef && twbRef.current) {
                  const text = twbRef.current;
                  const xOver = width - (labelX + text.x + text.width + 10);

                  if (xOver < 0) labelX -= text.width + text.x * 2;
                }

                return (
                  <>
                    <Grouped by="lookbackDays">
                      <LineSegments strokeWidth={1} compress={false} />
                    </Grouped>
                    {hoverDuration && hover ? (
                      <>
                        <line
                          x1={xScale({ duration: hoverDuration })}
                          x2={xScale({ duration: hoverDuration })}
                          y1={0}
                          y2={height}
                          stroke="rgba(0,0,0,0.5)"
                        />
                        <g transform={`translate(${labelX}, ${height + 18})`}>
                          <TextWithBackground
                            x={0}
                            y={0}
                            ref={twbRef}
                            fill="rgba(255,255,255,0.75)"
                            padding={5}
                          >
                            <tspan>{formatDuration(hoverDuration)}</tspan>
                          </TextWithBackground>
                        </g>
                      </>
                    ) : null}
                    <RecordHighlights
                      newRecords={newRecords}
                      chartData={chartData}
                    />
                  </>
                );
              }}
            </Coordinates>
          </SizedSVG>
        </DataContext.Provider>
      </div>
      {hoverDuration ? (
        <ul className="list-unstyled">
          {chartData
            .filter((d) => d.duration === hoverDuration)
            .sort((a, b) => a.lookbackDays - b.lookbackDays)
            .map(({ effort, lookbackDays, activityId }) => (
              <HoverItem
                key={lookbackDays}
                effort={effort}
                lookbackDays={lookbackDays}
                activityId={activityId}
                userId={userId}
              />
            ))}
        </ul>
      ) : null}
    </>
  );
}

function HoverItem({
  effort,
  lookbackDays,
  activityId,
  userId,
}: {
  effort: number;
  lookbackDays: number;
  activityId?: number;
  userId: number;
}) {
  const summary = useAPIData<Summary>(
    activityId ? `/activities/${userId}/${activityId}` : null,
  );

  const link = summary ? (
    <Link href={`/activity/${userId}/${activityId}`}>{summary.name}</Link>
  ) : null;

  return (
    <li>
      <span style={{ color: getLookbackColor(lookbackDays) }}>
        {formatLookback(lookbackDays)}
      </span>
      : {_.round(effort, 1)}% {link}
    </li>
  );
}

export default function BestEfforts({
  analysis,
  userId,
}: {
  analysis: Analysis;
  userId: number;
}): React.ReactElement | null {
  const { bestEfforts } = analysis;
  if (typeof bestEfforts === "undefined") {
    return null;
  }

  const durations = bestEfforts.durations;
  const newRecords = React.useMemo(
    () => findNewRecords(bestEfforts),
    [bestEfforts],
  );

  if (newRecords.length === 0) {
    return null; // Don't show the card if no new records
  }

  const chartData = transformToChartData(analysis);

  return (
    <div className="card mb-3">
      <div className="card-body">
        <h5 className="card-title">New Best Efforts</h5>
        <BestEffortsList newRecords={newRecords} />
        <BestEffortsChart
          chartData={chartData}
          durations={durations}
          newRecords={newRecords}
          userId={userId}
        />
      </div>
    </div>
  );
}

export function BestEffortsList({ newRecords }: { newRecords: NewRecord[] }) {
  return (
    <ul className="list-unstyled">
      {newRecords.map((record, idx) => (
        <li
          key={idx}
          className="mb-1"
          style={{ paddingLeft: "1.5em", textIndent: "-1.5em" }}
        >
          <span style={{ marginRight: "0.5em" }}>🌟</span>
          Best {formatDuration(record.duration)} effort in{" "}
          {formatLookback(record.lookbackDays)}
        </li>
      ))}
    </ul>
  );
}
